import React, { useMemo } from 'react';
import { Nullable } from 'global';
import { formatCurrency } from 'utils';
import { Box, Typography } from '@mui/material';
import { blue, blueGrey } from '@mui/material/colors';
import { Column } from 'components/shared/TableListing/types';

import { getRowKey } from '../../../../lib';
import { QuoteCalcColumnTotals, QuoteCalcRowResult, QuoteCalcViewModel } from '../../../../api/types';
import { QuoteChargeListingContext, QuoteChargeListingEntity } from '../../../../types';
import { roundToCents } from '../../../../../dealBuilder/lib';

interface Props {
  data: QuoteChargeListingEntity[];
  priceCalc: Nullable<QuoteCalcViewModel>;
  columns: Column<QuoteChargeListingEntity, QuoteChargeListingContext>[];
}

const emptyColumnTotals = (): QuoteCalcColumnTotals => ({
  total: 0,
  markup: 0,
  discount: 0,
  salesTax: 0,
  netPrice: 0,
  retailPrice: 0,
  wholesalePrice: 0,
});

export const GroupSubtotalRow = ({ data, columns, priceCalc }: Props) => {
  const subtotal = useMemo<QuoteCalcColumnTotals>(() => {
    if (!priceCalc) return emptyColumnTotals();

    const byKey = new Map<string, QuoteCalcRowResult>();
    priceCalc.rows.forEach(row => {
      if (row.key) byKey.set(row.key, row);
    });

    return data.reduce<QuoteCalcColumnTotals>((acc, entity) => {
      const key = getRowKey(entity);
      const row = key ? byKey.get(key) : undefined;

      if (!row) return acc;

      const { columnTotals } = row;

      return {
        markup: acc.markup + columnTotals.markup,
        discount: acc.discount + columnTotals.discount,
        salesTax: acc.salesTax + columnTotals.salesTax,
        netPrice: acc.netPrice + columnTotals.netPrice,
        total: roundToCents(acc.total + columnTotals.total),
        retailPrice: acc.retailPrice + columnTotals.retailPrice,
        wholesalePrice: acc.wholesalePrice + columnTotals.wholesalePrice,
      };
    }, emptyColumnTotals());
  }, [data, priceCalc]);

  return (
    <Box className="listing-section">
      {columns.map((column, index) => (
        <Box
          key={`${column.renderKey}_${index}`}
          className="listing-cell"
          style={{ minHeight: 'auto', padding: '0.5rem 1rem' }}
          sx={{ backgroundColor: blue[50], color: blueGrey[800], fontWeight: 600 }}
        >
          {column.renderKey === 'type' ? (
            <Typography variant="subtitle2" component="span">
              Subtotal
            </Typography>
          ) : (
            column.renderKey && (
              <Typography variant="subtitle2">
                {formatCurrency(subtotal[column.renderKey as keyof QuoteCalcColumnTotals] ?? null, true)}
              </Typography>
            )
          )}
        </Box>
      ))}
    </Box>
  );
};
